import { useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { ModifyProcessCommand, ProjectTemplateProcessResource, ProjectTemplateResource } from "@octopusdeploy/octopus-server-client";
import { useMemo } from "react";
import { toProjectTemplateProcessIdentifier } from "~/areas/projects/components/Process/types";

export default function useProjectTemplateDeploymentProcessState(projectTemplate: ProjectTemplateResource, initialResult: ProjectTemplateProcessResource, gitRef: string) {
    const { result: deploymentProcess, refetch: reloadDeploymentProcess } = useQuery(
        async (repository) => await repository.ProjectTemplates.getDeploymentProcess(projectTemplate.Slug, gitRef),
        [projectTemplate.Slug, gitRef],
        "Load project template deployment process",
        {
            initialResult,
        }
    );

    const { execute: modifyDeploymentProcess } = useMutation({
        name: "Modify project template deployment process",
        action: async (repository, process: ModifyProcessCommand, gitRef: string | undefined) => {
            if (!("ProjectTemplateSlug" in process)) {
                throw new Error("Expected ModifyProjectTemplateProcessCommand but received a different process command type");
            }
            await repository.ProjectTemplates.modifyDeploymentProcess(process, projectTemplate.Slug, gitRef);
            reloadDeploymentProcess();
        },
    });

    const deploymentProcessIdentifier = useMemo(() => {
        return toProjectTemplateProcessIdentifier(projectTemplate.Slug, gitRef);
    }, [projectTemplate.Slug, gitRef]);

    return {
        deploymentProcess,
        deploymentProcessIdentifier,
        reloadDeploymentProcess,
        modifyDeploymentProcess,
    };
}
